import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Switch } from '@finn-auto/design-system/atoms/switch';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';

/**
 * What a candidate sees, in the six landing states of mockup v0.5 (design v0.6 §4 and §6). A preview
 * only: the state buttons are a demo control (in production the link itself decides the state), and
 * {placeholders} stand where a real person's details will be. The buttons inside the preview walk
 * through the flows: renewal → renewed or deleted; Interested → pop-up → in process.
 */
type State = 'profile' | 'renew' | 'interest' | 'applied' | 'renewed' | 'deleted';

const STATES: [State, string][] = [
  ['profile', 'Manage profile'],
  ['renew', 'Renewal link (from email)'],
  ['interest', 'Role interest (from email)'],
  ['applied', 'In process'],
  ['renewed', 'Consent renewed'],
  ['deleted', 'Data deleted'],
];

const HEAD: Record<State, { h: string; p: string; status: string; valid: string }> = {
  profile: {
    h: 'Hi {first name} — you’re still on our radar.',
    p: 'We’d like to stay in touch properly. Here is everything we hold about you, and every switch is yours.',
    status: 'In the Talent Community',
    valid: '{expiry date}',
  },
  renew: {
    h: 'Hi {first name} — your consent expires on {expiry date}.',
    p: 'To keep you in the FINN Talent Community we need you to confirm. Check your choices below first, then confirm.',
    status: 'Renewal due',
    valid: '{expiry date}',
  },
  interest: {
    h: 'Hi {first name} — {role} is open again.',
    p: 'You were one of the strongest candidates we met for this role. Confirm below if you would like to pick the conversation back up.',
    status: 'In the Talent Community',
    valid: '{expiry date}',
  },
  applied: {
    h: 'You’re back in the process, {first name}.',
    p: 'Your application for {role} is with the team.',
    status: 'In the hiring process',
    valid: '—',
  },
  renewed: {
    h: 'Thank you, {first name} — you’re staying on our radar.',
    p: 'Your consent is renewed for another 24 months. Everything we hold and every switch is still yours, below.',
    status: 'In the Talent Community',
    valid: '{new expiry date}',
  },
  deleted: {
    h: 'Your data is being deleted.',
    p: 'We’ve received your request. You don’t need to do anything else.',
    status: 'Deletion in progress',
    valid: '—',
  },
};

function SwitchLine({ title, body, checked, onChange, note }: { title: string; body: string; checked: boolean; onChange: (v: boolean) => void; note?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-solid border-zinc-100 py-3">
      <div>
        <p className="text-sm font-medium text-zinc-900">
          {title} {note && <span className="ml-1 text-xs font-normal text-zinc-500">{note}</span>}
        </p>
        <p className="text-sm text-zinc-600">{body}</p>
      </div>
      <Switch checked={checked} onCheckedChange={(v) => onChange(v === true)} aria-label={title} />
    </div>
  );
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-zinc-500">{children}</p>
);
const Ok = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-5 rounded border border-solid border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">{children}</p>
);

export function PortalPreviewTab() {
  const [state, setState] = useState<State>('profile');
  const [jobs, setJobs] = useState(true);
  const [news, setNews] = useState(false);
  const [renewJobs, setRenewJobs] = useState(true);
  const [renewNews, setRenewNews] = useState(false);
  const [popup, setPopup] = useState(false);
  const head = HEAD[state];

  function go(s: State) {
    setState(s);
    setPopup(s === 'interest');
    if (s === 'renew') {
      setRenewJobs(true);
      setRenewNews(news);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded border border-dashed border-zinc-300 bg-zinc-50 p-3 text-sm">
        <span className="text-zinc-500">Preview state</span>
        {STATES.map(([s, label]) => (
          <button
            key={s}
            type="button"
            aria-pressed={state === s}
            onClick={() => go(s)}
            className={state === s ? 'rounded bg-zinc-900 px-3 py-1 text-white' : 'rounded px-3 py-1 text-zinc-700 hover:bg-zinc-200'}
          >
            {label}
          </button>
        ))}
        <span className="text-xs text-zinc-500">· demo control, not part of the portal</span>
      </div>

      <div className="mx-auto max-w-2xl overflow-hidden rounded-lg border border-solid border-pearl bg-white">
        <div className="bg-zinc-900 px-8 pb-8 pt-6 text-white">
          <p className="text-lg font-bold tracking-tight">FINN</p>
          <p className="mt-10 text-xs uppercase tracking-wide text-zinc-300">FINN Talent Community</p>
          <h2 className="mt-2 text-2xl font-semibold">{head.h}</h2>
          <p className="mt-2 text-sm text-zinc-300">{head.p}</p>
        </div>

        <div className="px-8 py-6">
          <div className="mb-5 grid grid-cols-3 gap-3 rounded border border-solid border-zinc-200 p-3 text-sm">
            <div>
              <p className="text-xs text-zinc-500">Your status</p>
              <p className={state === 'renew' ? 'font-medium text-amber-800' : 'font-medium text-green-800'}>{head.status}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Consent valid until</p>
              <p className="font-medium text-zinc-900">{head.valid}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-500">Last contact from us</p>
              <p className="font-medium text-zinc-900">{'{date}'}</p>
            </div>
          </div>

          {state === 'profile' && (
            <p className="mb-5 rounded bg-zinc-50 px-4 py-3 text-sm text-zinc-700">
              We will ask before extending your consent — never automatically. If you ignore that email, your data is deleted.
            </p>
          )}

          {(state === 'interest' || state === 'applied') && (
            <div className="mb-5 rounded border border-solid border-zinc-200 p-4">
              <p className="font-semibold text-zinc-900">{'{role}'}</p>
              <p className="text-sm text-zinc-500">{'{location} · {team} · posted via Lever'}</p>
            </div>
          )}

          {state === 'applied' && (
            <>
              <Ok>
                ✓ You’re back in the process for <b>{'{role}'}</b>. You are now in FINN’s applicant tracking system, and your
                recruiter, <b>{'{recruiter}'}</b>, will contact you about the next steps.
              </Ok>
              <Label>What happens now</Label>
              <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
                <li>Your recruiter reviews your profile and gets in touch with next steps</li>
                <li>We pause all other Talent Community emails while you are in the process</li>
                <li>You can withdraw from the process at any time by replying to your recruiter</li>
              </ul>
            </>
          )}

          {state === 'renew' && (
            <div className="rounded border border-solid border-zinc-200 p-5">
              <p className="font-semibold text-zinc-900">Stay in the FINN Talent Community?</p>
              <p className="mt-1 text-sm text-zinc-600">
                Your consent expires on <b>{'{expiry date}'}</b>. Confirm to stay for another 24 months — or tell us to delete
                your data now. If we don’t hear from you by that date, we delete it automatically. Nothing happens until you
                choose.
              </p>
              <Label>Your choices — change them here any time</Label>
              <SwitchLine
                title="Matching job opportunities"
                note="needed to stay in the Talent Community"
                body="A recruiter writes to you personally when a role genuinely fits. Roughly two or three times a year."
                checked={renewJobs}
                onChange={setRenewJobs}
              />
              <SwitchLine
                title="FINN team updates & newsletter"
                body="What we’re building and how the team is growing. Monthly, and you can stop it any time."
                checked={renewNews}
                onChange={setRenewNews}
              />
              {!renewJobs && (
                <p className="mt-3 text-sm text-amber-800">
                  Switching off job opportunities means we have no basis to keep your profile, so your data would be deleted.
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    if (!renewJobs) return go('deleted');
                    setNews(renewNews);
                    go('renewed');
                  }}
                >
                  {renewJobs ? 'Yes, keep me in the Talent Community' : 'Delete my data'}
                </Button>
                {renewJobs && (
                  <Button size="sm" variant="secondary" onClick={() => go('deleted')}>
                    No, delete my data
                  </Button>
                )}
              </div>
            </div>
          )}

          {state === 'renewed' && (
            <Ok>
              ✓ Thank you — your consent is renewed for 24 months, until <b>{'{new expiry date}'}</b>. You’ll hear from us
              about <b>{news ? 'job opportunities and the team newsletter' : 'job opportunities only'}</b>, and you can change
              that below at any time. We’ll ask again about two months before it expires.
            </Ok>
          )}

          {state === 'deleted' ? (
            <>
              <Ok>✓ Your deletion request is confirmed. Nothing further is needed from you.</Ok>
              <Label>What happens now</Label>
              <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
                <li>Your profile, CV and files are removed within 24 hours</li>
                <li>Your entries in our search index are removed</li>
                <li>We stop all contact, and cancel any scheduled emails</li>
                <li>We email you a confirmation once it’s complete</li>
              </ul>
              <p className="mt-4 text-sm text-zinc-500">
                Changed your mind? You can join the FINN Talent Community again at any time via the careers page. Your earlier
                data can’t be restored.
              </p>
            </>
          ) : (
            state !== 'renew' &&
            state !== 'applied' && (
              <>
                <Label>What we store about you</Label>
                <div className="rounded bg-zinc-50 p-4 text-sm text-zinc-700">
                  Your CV · contact details · confirmed skills · your application history at FINN · the interview feedback
                  from your process.
                  <p className="mt-1 text-xs text-zinc-500">
                    We use AI to widen search words when a recruiter looks for a role. A human recruiter always decides who to
                    contact.
                  </p>
                  <p className="mt-2 font-medium text-blue-700">Download everything we hold →</p>
                </div>
                <Label>Keep it current</Label>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" disabled>
                    Upload new CV
                  </Button>
                  <Button size="sm" variant="secondary" disabled>
                    Update skills
                  </Button>
                  <Button size="sm" variant="secondary" disabled>
                    Change availability
                  </Button>
                </div>
                <Label>What you hear from us</Label>
                <SwitchLine
                  title="Matching job opportunities"
                  body="A recruiter writes to you personally when a role genuinely fits. Roughly two or three times a year."
                  checked={jobs}
                  onChange={setJobs}
                />
                <SwitchLine
                  title="FINN team updates & newsletter"
                  body="What we’re building and how the team is growing. Monthly, and you can stop it any time."
                  checked={news}
                  onChange={setNews}
                />
                <Label>Leave the Talent Community</Label>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="max-w-sm text-sm text-zinc-600">
                    Deletes your profile, your files and your search entries within 24 hours. We email you a confirmation.
                  </p>
                  <Button size="sm" variant="secondary" onClick={() => go('deleted')}>
                    Withdraw &amp; delete
                  </Button>
                </div>
              </>
            )
          )}
          <p className="mt-8 text-xs text-zinc-400">FINN GmbH · Privacy notice · Your data is stored in the EU. Questions? talent@finn.com</p>
        </div>
      </div>

      {popup && state === 'interest' && (
        <Modal open onOpenChange={(next: boolean) => !next && setPopup(false)}>
          <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
            <ModalHeader>
              <ModalTitle>Start your application?</ModalTitle>
            </ModalHeader>
            <p className="text-sm text-zinc-700">
              You’re about to apply for <b>{'{role}'}</b>.
            </p>
            <p className="text-sm text-zinc-700">
              With this, you are <b>officially in FINN’s applicant tracking system again</b> and the hiring process starts. Your
              recruiter, {'{recruiter}'}, will contact you about the next steps.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => go('applied')}>
                Yes, start the process
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setPopup(false)}>
                Not right now
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
}
